import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { Textarea } from '@/components/ui/Textarea'
import RadioCircle from '@/components/ui/RadioCircle'
import { approve_submission_path, request_changes_submission_path } from '@/lib/routes'

type Verdict = 'approve' | 'request_changes'

// The reviewer's verdict panel, GitHub's "Finish your review": one comment
// box, then a verdict choice. Approve's note is optional (it lands in the
// activity feed); Request changes needs one — the doer has to know what to
// fix. No verdict preselected: publishing a plan is not a default.
export default function ReviewSubmitPopover({ submissionId, planName, revision = false, approveBackTo, sendBackBackTo }: {
  submissionId: string
  planName: string
  /** The submission revises an already-published plan: approving publishes it as a new version. */
  revision?: boolean
  /** In-app path to land on after approving. */
  approveBackTo: string
  /** In-app path to land on after sending the work back. */
  sendBackBackTo: string
}) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [verdict, setVerdict] = useState<Verdict | null>(null)
  const ready = verdict === 'approve' || (verdict === 'request_changes' && note.trim().length > 0)

  const submit = () => {
    if (verdict === 'approve') {
      router.post(approve_submission_path(submissionId), { note, back_to: approveBackTo }, { preserveScroll: true })
    } else {
      router.post(request_changes_submission_path(submissionId), { note, back_to: sendBackBackTo }, { preserveScroll: true })
    }
    setOpen(false)
  }

  const option = (value: Verdict, label: string, description: string) => (
    <button
      type="button"
      onClick={() => setVerdict(value)}
      className="flex items-start gap-2.5 rounded-md p-2 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="mt-0.5"><RadioCircle active={verdict === value} /></span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        <span className="block text-[13px] text-muted-foreground">{description}</span>
      </span>
    </button>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" className="shrink-0">Submit review</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-[480px] flex-col gap-3 p-4">
        <p className="text-base font-semibold text-foreground">Finish your review</p>
        <Textarea
          rows={5}
          className="min-h-28 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-chart-muted"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Leave a comment"
          autoFocus
        />
        <div className="flex flex-col">
          {option('approve', 'Approve', revision
            ? `Update the published ${planName} with these changes and complete the task.`
            : `Publish ${planName} and complete the task.`)}
          {option('request_changes', 'Request changes', 'Send the plan back with your notes (required).')}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" disabled={!ready} onClick={submit}>Submit review</Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
